import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { Box, Paper, TextField, Typography, Divider, Button } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatContent from './chat-content.component';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { messagesTemplate, createMessageTemplate } from 'apollo/templates/message';
import { getEscapedMessage } from 'utils/escaped-message';
import { Message } from 'models/message';
import { animateScroll } from 'react-scroll';

type ChatProps = {
    orderId: string;
    isImportedOrder: boolean | undefined;
};

const Chat: FC<ChatProps> = ({ orderId, isImportedOrder }: ChatProps) => {
    const { t: translate } = useTranslation(['messages']);
    const [noOfMessageRows, setNoOfMessageRows] = useState(1);
    const [messages, setMessages] = useState<Message[]>([] as Message[]);
    const [messageValue, setMessageValue] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    const scrollToBottom = (duration = 200) => {
        animateScroll.scrollToBottom({
            containerId: 'messages-container',
            smooth: false,
            duration: duration,
        });
    };

    useEffect(() => {
        if (messages.length) {
            scrollToBottom(0);
        }
    }, [messages]);

    useQuery(messagesTemplate, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        variables: {
            orderId: orderId,
        },
        onCompleted: ({ messages }) => {
            setMessages(messages);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [sendMessage] = useMutation(createMessageTemplate, {
        onError: (err) => {
            console.log(err, 'error');
        },
        onCompleted: ({ createMessage }) => {
            setMessages([...messages, createMessage]);
            setMessageValue('');
            scrollToBottom();
            setNoOfMessageRows(1);
        },
    });

    const onMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        setMessageValue(event.target.value);
    };

    const onSendMessage = async () => {
        const formattedMessage = getEscapedMessage(messageValue);
        const messageInput = {
            orderId: orderId,
            message: formattedMessage,
            type: 'LAB_INTERNAL_ORDER',
        };
        await sendMessage({
            variables: { messageInput },
        });
    };

    return (
        <Paper variant="outlined" square>
            <Box p={2}>
                <Box mb={1}>
                    <Typography variant="h3">{translate('messages:LAB_CHAT')}</Typography>
                </Box>
                <Divider />
                <Box id="messages-container" pt={1} sx={{ height: 350, overflowY: 'auto' }}>
                    {messages?.map((message) => (
                        <Box key={message._id}>
                            <ChatContent message={message} />
                        </Box>
                    ))}
                    <Box id="messages-end" />
                </Box>
                <Box mb={1}>
                    <Divider />
                </Box>
                <Box mb={1}>
                    <TextField
                        fullWidth
                        multiline
                        placeholder={translate('messages:TYPE_CHAT_MESSAGE')}
                        required
                        value={messageValue}
                        onChange={onMessageChange}
                        rows={noOfMessageRows}
                        onFocus={() => setNoOfMessageRows(6)}
                        onBlur={() => (!messageValue ? setNoOfMessageRows(1) : null)}
                    />
                </Box>
                {!isImportedOrder ? (
                    <Button
                        onClick={onSendMessage}
                        disabled={!messageValue}
                        variant="contained"
                        startIcon={<SendIcon />}
                    >
                        {translate('messages:SEND')}
                    </Button>
                ) : undefined}
            </Box>
        </Paper>
    );
};

export default Chat;
